<template>
  <div class="flex flex-col w-full h-full">
    <!-- Header -->
    <div class="mb-4">
      <h2 class="text-xl font-semibold">
        Set Group Price
      </h2>
      <p class="text-sm text-gray-600 mt-1">
        Set a fixed price for "{{ groupName }}" that will override all individual item pricing, or remove it to use dynamic pricing.
      </p>
    </div>

    <!-- Price Input -->
    <div class="mb-4">
      <div class="field">
        <label
          for="groupPrice"
          class="block text-sm font-medium mb-1"
        >Group Price</label>
        <span class="p-input-icon-left w-full">
          <i class="pi pi-pound" />
          <InputNumber 
            id="groupPrice"
            v-model="groupPrice" 
            :min="0" 
            :max="999999"
            mode="currency"
            currency="GBP"
            locale="en-GB"
            class="w-full"
            placeholder="Enter group price..."
            @update:modelValue="handlePriceUpdate"
          />
        </span>
        <small class="text-gray-500">Enter 0 or leave empty to remove the group price and use dynamic pricing</small>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-between gap-2 mt-auto">
      <Button 
        v-if="currentGroupPrice > 0"
        label="Remove Group Price" 
        icon="pi pi-trash"
        severity="danger"
        text
        @click="removeGroupPrice"
      />
      <div class="flex gap-2 ml-auto">
        <Button 
          label="Cancel" 
          icon="pi pi-times"
          severity="secondary"
          @click="closeModal"
        />
        <Button 
          label="Save" 
          icon="pi pi-check"
          :disabled="!hasChanges"
          :loading="isSubmitting"
          @click="saveGroupPrice"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import { useToastService } from '@/composables/useToastService';
import { projectStore } from '../projectStore';

const props = defineProps({
    groupId: { type: [Number, String], required: true },
    groupName: { type: String, required: true },
    currentPrice: { type: Number, default: 0 },
    equipmentListId: { type: [Number, String], required: true },
    currentPricingMode: { type: String, default: 'dynamic' },
    isPricingLocked: { type: Boolean, default: false }
});

const emit = defineEmits(['close', 'price-updated']);

const toast = useToastService();

const groupPrice = ref(0);
const originalPrice = ref(0);
const isSubmitting = ref(false);

const currentGroupPrice = computed(() => parseFloat(props.currentPrice) || 0);

const hasChanges = computed(() => {
    return groupPrice.value !== originalPrice.value;
});

const handlePriceUpdate = (value) => {
    // Force reactive update to ensure hasChanges computed property updates immediately
    groupPrice.value = value;
};

const saveGroupPrice = async () => {
    if (!hasChanges.value) return;
    
    isSubmitting.value = true;
    
    try {
        const projectId = projectStore.currentProjectId;
        const equipmentListId = props.equipmentListId;
        const groupId = props.groupId;
        
        // Determine pricing mode and price based on input
        let pricingMode;
        let priceValue;
        
        if (groupPrice.value > 0) {
            // Setting a group price - use custom mode
            pricingMode = 'custom';
            priceValue = groupPrice.value;
        } else {
            // Removing group price - use dynamic mode
            pricingMode = 'dynamic';
            priceValue = null;
        }
        
        const updateData = {
            price: priceValue,
            pricing_mode: pricingMode
        };
        
        const { updateEquipmentListContent } = await import('@/services/projectService');
        await updateEquipmentListContent(projectId, equipmentListId, groupId, updateData);
        
        // Emit success event
        emit('price-updated', {
            groupId: props.groupId,
            price: priceValue
        });
        
        toast.add({
            severity: 'success',
            summary: 'Group Price Updated',
            detail: `Group price ${priceValue ? `set to £${priceValue.toFixed(2)}` : 'removed'}`,
            life: 3000
        });
        
        closeModal();
        
    } catch (error) {
        console.error('Error updating group price:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to update group price: ${error.message}`,
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

const removeGroupPrice = () => {
    groupPrice.value = null; // This will trigger dynamic mode when saved
};

const closeModal = () => {
    emit('close');
};

onMounted(() => {
    // Initialize with current price
    groupPrice.value = currentGroupPrice.value;
    originalPrice.value = currentGroupPrice.value;
});
</script>